import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import AccountSignIn from './AccountSignIn';
import { useTheme } from '../ThemeContext';
import { showToast } from './Toast';

// Same rules as storedLogoDimensions in src/shared/logoFit.js.
// This file must not import that module: webpack then treats the CommonJS
// email layout, which requires it, as an ES module with no default export.
function positiveLogoPixel(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  const rounded = Math.round(number);
  if (rounded <= 0 || rounded > 20000) return 0;
  return rounded;
}

function storedLogoDimensions(naturalWidth, naturalHeight) {
  const width = positiveLogoPixel(naturalWidth);
  const height = positiveLogoPixel(naturalHeight);
  if (!width || !height) return { logo_width: null, logo_height: null };
  return { logo_width: width, logo_height: height };
}

const Profile = ({ profile, onProfileSaved }) => {
  const { colors } = useTheme();
  const [savedSection, setSavedSection] = useState('');
  const savedTimer = useRef(null);

  useEffect(() => () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  const showSaved = (section) => {
    setSavedSection(section);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedSection(''), 2500);
  };
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    team_name: '',
    brokerage: '',
    phone: '',
    email: '',
    website: '',
    market: '',
    logo_url: '',
    headshot_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        team_name: profile.team_name || '',
        brokerage: profile.brokerage || '',
        phone: profile.phone || '',
        email: profile.email || '',
        website: profile.website || '',
        market: profile.market || '',
        logo_url: profile.logo_url || '',
        headshot_url: profile.headshot_url || ''
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const readLocalImageSize = (file) => new Promise((resolve) => {
    if (!file || typeof URL === 'undefined' || typeof Image === 'undefined') {
      resolve({ width: 0, height: 0 });
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    const finish = (width, height) => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width, height });
    };
    image.onload = () => finish(image.naturalWidth, image.naturalHeight);
    image.onerror = () => finish(0, 0);
    image.src = objectUrl;
  });

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      showToast('Please upload a JPEG, PNG, or HEIC file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('Image file size must be 10 MB or less');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${fieldName}_${Date.now()}.${fileExt}`;
      const filePath = `${profile.id}/${fileName}`;

      // Upload to Supabase Storage
      const { error } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      // Update form data. Logo pixel size is saved with the file so
      // outgoing email can keep the image's proportions.
      const patch = { [fieldName]: urlData.publicUrl };
      if (fieldName === 'logo_url') {
        const measured = await readLocalImageSize(file);
        Object.assign(patch, storedLogoDimensions(measured.width, measured.height));
      }
      setFormData(prev => ({ ...prev, [fieldName]: urlData.publicUrl }));
      setUploadProgress(100);

      // Auto-save after upload. Stay on this page.
      await handleSubmit(null, patch, 'branding');

    } catch (error) {
      console.error('Error uploading file:', error);
      showToast('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e, overrideData = null, section = 'profile') => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);

    try {
      const dataToSave = overrideData || formData;
      const { error } = await supabase
        .from('profiles')
        .update(dataToSave)
        .eq('user_id', profile.user_id);

      if (error) throw error;

      if (onProfileSaved) onProfileSaved(dataToSave);
      showSaved(section);
    } catch (error) {
      console.error('Error saving profile:', error);
      showToast('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 style={{...styles.pageTitle, color: colors.text}}>My Profile</h2>

      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
        <h3 style={{...styles.cardTitle, color: colors.text}}>Agent Information</h3>
        <form onSubmit={(event) => handleSubmit(event, null, 'profile')} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>First Name *</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Last Name *</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Team/Group Name</label>
            <input
              type="text"
              name="team_name"
              value={formData.team_name}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Brokerage</label>
            <input
              type="text"
              name="brokerage"
              value={formData.brokerage}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Phone *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Website</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Market/Location *</label>
            <input
              type="text"
              name="market"
              value={formData.market}
              onChange={handleChange}
              style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
              required
            />
          </div>

          <div style={styles.saveRow}>
            <button type="submit" style={styles.btnPrimary} disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            {savedSection === 'profile' ? <span role="status">Saved</span> : null}
          </div>
        </form>
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${colors.border}` }}>
          <h3 style={{ color: colors.text, marginTop: 0 }}>Sign-in</h3>
          <p style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
            Change the email and password you use to sign in.
          </p>
          <AccountSignIn mode="agent" email={profile.email} colors={colors} />
        </div>
      </div>

      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
        <h3 style={{...styles.cardTitle, color: colors.text}}>Branding</h3>
        <div style={styles.form}>
          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Headshot Photo</label>

            <div style={styles.uploadContainer}>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                onChange={(e) => handleFileUpload(e, 'headshot_url')}
                style={styles.fileInput}
                id="headshot-upload"
                disabled={uploading}
              />
              <label htmlFor="headshot-upload" style={styles.uploadButton}>
                {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Photo (JPEG/PNG/HEIC)'}
              </label>
            </div>

            {formData.headshot_url && (
              <div style={{...styles.imagePreview, background: colors.surfaceAlt}}>
                <img
                  src={formData.headshot_url}
                  alt="Headshot preview"
                  style={styles.headshotImage}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{...styles.imageError, display: 'none'}}>
                  Invalid image URL
                </div>
              </div>
            )}
            <p style={{...styles.helpText, color: colors.textSecondary}}>
              Max file size: 10 MB. Accepts JPEG, PNG, and HEIC formats.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={{...styles.label, color: colors.text}}>Brokerage Logo</label>

            <div style={styles.uploadContainer}>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                onChange={(e) => handleFileUpload(e, 'logo_url')}
                style={styles.fileInput}
                id="logo-upload"
                disabled={uploading}
              />
              <label htmlFor="logo-upload" style={styles.uploadButton}>
                {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Logo (JPEG/PNG/HEIC)'}
              </label>
            </div>

            {formData.logo_url && (
              <div style={{...styles.imagePreview, background: colors.surfaceAlt}}>
                <img
                  src={formData.logo_url}
                  alt="Logo preview"
                  style={styles.logoImage}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{...styles.imageError, display: 'none'}}>
                  Invalid image URL
                </div>
              </div>
            )}
            <p style={{...styles.helpText, color: colors.textSecondary}}>
              Max file size: 10 MB. Accepts JPEG, PNG, and HEIC formats.
            </p>
          </div>

          <div style={styles.saveRow}>
            <button
              type="button"
              style={styles.btnPrimary}
              onClick={(event) => handleSubmit(event, null, 'branding')}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
            {savedSection === 'branding' ? <span role="status">Saved</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageTitle: {
    marginBottom: '1.5rem',
    fontSize: '1.8rem'
  },
  card: {
    background: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '2rem'
  },
  cardTitle: {
    marginBottom: '1.5rem',
    fontSize: '1.2rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    fontSize: '1rem'
  },
  colorPicker: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center'
  },
  colorInput: {
    width: '60px',
    height: '40px',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer'
  },
  colorPreview: {
    width: '60px',
    height: '40px',
    borderRadius: '0.5rem',
    border: '2px solid #ddd'
  },
  imagePreview: {
    marginTop: '1rem',
    padding: '1rem',
    background: '#f9f9f9',
    borderRadius: '0.5rem',
    textAlign: 'center'
  },
  headshotImage: {
    maxWidth: '150px',
    maxHeight: '150px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #ddd'
  },
  logoImage: {
    maxWidth: '200px',
    maxHeight: '100px',
    objectFit: 'contain'
  },
  imageError: {
    color: '#59c4c4',
    fontSize: '0.9rem',
    padding: '1rem'
  },
  helpText: {
    fontSize: '0.85rem',
    color: '#666',
    marginTop: '0.5rem',
    fontStyle: 'italic'
  },
  uploadContainer: {
    marginBottom: '1rem'
  },
  fileInput: {
    display: 'none'
  },
  uploadButton: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1rem',
    textAlign: 'center'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1rem',
    alignSelf: 'flex-start'
  },
  saveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  }
};

export default Profile;
